import type { Candle, CandleInterval } from '../types';

const DEFAULT_TENKAN_PERIOD = 9;
const DEFAULT_KIJUN_PERIOD = 26;
const DEFAULT_SENKOU_B_PERIOD = 52;
const DEFAULT_DISPLACEMENT = 26;

interface ComputeIchimokuCloudParams {
  candles: Candle[];
  interval: CandleInterval;
  tenkanPeriod?: number;
  kijunPeriod?: number;
  senkouBPeriod?: number;
  displacement?: number;
}

export interface IchimokuCloudOverlayResult {
  projectedCandles: Candle[];
  tenkan: (number | undefined)[];
  kijun: (number | undefined)[];
  senkouA: (number | undefined)[];
  senkouB: (number | undefined)[];
  chikou: (number | undefined)[];
  bullishCloudUpper: (number | undefined)[];
  bullishCloudLower: (number | undefined)[];
  bearishCloudUpper: (number | undefined)[];
  bearishCloudLower: (number | undefined)[];
}

function clampInteger(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

function normalizePeriod(value: number | undefined, fallback: number): number {
  if (!Number.isFinite(value)) {
    return fallback;
  }
  return clampInteger(value as number, 1, 500);
}

function intervalMilliseconds(interval: CandleInterval): number | null {
  if (interval === '1m') {
    return 60_000;
  }
  if (interval === '3m') {
    return 3 * 60_000;
  }
  if (interval === '5m') {
    return 5 * 60_000;
  }
  if (interval === '15m') {
    return 15 * 60_000;
  }
  if (interval === '30m') {
    return 30 * 60_000;
  }
  if (interval === '45m') {
    return 45 * 60_000;
  }
  if (interval === '1h') {
    return 60 * 60_000;
  }
  if (interval === '2h') {
    return 2 * 60 * 60_000;
  }
  if (interval === '3h') {
    return 3 * 60 * 60_000;
  }
  if (interval === '4h') {
    return 4 * 60 * 60_000;
  }
  if (interval === '1d') {
    return 24 * 60 * 60_000;
  }
  if (interval === '1w') {
    return 7 * 24 * 60 * 60_000;
  }
  return null;
}

function addUtcMonths(timestampMs: number, monthsToAdd: number): number {
  const date = new Date(timestampMs);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();
  const milliseconds = date.getUTCMilliseconds();

  const totalMonths = month + monthsToAdd;
  const targetYear = year + Math.floor(totalMonths / 12);
  const targetMonth = ((totalMonths % 12) + 12) % 12;
  const maxDayInMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const clampedDay = Math.min(day, maxDayInMonth);

  return Date.UTC(
    targetYear,
    targetMonth,
    clampedDay,
    hours,
    minutes,
    seconds,
    milliseconds,
  );
}

function addInterval(timestampMs: number, interval: CandleInterval, steps: number): number {
  if (interval === '1M') {
    return addUtcMonths(timestampMs, steps);
  }

  const intervalMs = intervalMilliseconds(interval);
  if (intervalMs === null) {
    return timestampMs;
  }

  return timestampMs + intervalMs * steps;
}

function buildProjectedCandles(
  candles: Candle[],
  interval: CandleInterval,
  displacement: number,
): Candle[] {
  if (candles.length === 0 || displacement <= 0) {
    return [...candles];
  }

  const lastCandle = candles[candles.length - 1];
  const lastTimestampMs = Date.parse(lastCandle.time);
  if (!Number.isFinite(lastTimestampMs)) {
    return [...candles];
  }

  const projectedCandles = [...candles];

  for (let step = 1; step <= displacement; step += 1) {
    const projectedTimestampMs = addInterval(lastTimestampMs, interval, step);
    projectedCandles.push({
      time: new Date(projectedTimestampMs).toISOString(),
      open: lastCandle.close,
      high: lastCandle.close,
      low: lastCandle.close,
      close: lastCandle.close,
      volume: 0,
    });
  }

  return projectedCandles;
}

function midpointHighLow(candles: Candle[], endIndex: number, period: number): number | undefined {
  if (endIndex < period - 1) {
    return undefined;
  }

  let highest = Number.NEGATIVE_INFINITY;
  let lowest = Number.POSITIVE_INFINITY;

  for (let index = endIndex - period + 1; index <= endIndex; index += 1) {
    const candle = candles[index];
    if (candle.high > highest) {
      highest = candle.high;
    }
    if (candle.low < lowest) {
      lowest = candle.low;
    }
  }

  if (!Number.isFinite(highest) || !Number.isFinite(lowest)) {
    return undefined;
  }

  return (highest + lowest) / 2;
}

export function computeIchimokuCloudOverlay({
  candles,
  interval,
  tenkanPeriod = DEFAULT_TENKAN_PERIOD,
  kijunPeriod = DEFAULT_KIJUN_PERIOD,
  senkouBPeriod = DEFAULT_SENKOU_B_PERIOD,
  displacement = DEFAULT_DISPLACEMENT,
}: ComputeIchimokuCloudParams): IchimokuCloudOverlayResult {
  const normalizedTenkanPeriod = normalizePeriod(tenkanPeriod, DEFAULT_TENKAN_PERIOD);
  const normalizedKijunPeriod = normalizePeriod(kijunPeriod, DEFAULT_KIJUN_PERIOD);
  const normalizedSenkouBPeriod = normalizePeriod(senkouBPeriod, DEFAULT_SENKOU_B_PERIOD);
  const normalizedDisplacement = Number.isFinite(displacement)
    ? clampInteger(displacement, 0, 500)
    : DEFAULT_DISPLACEMENT;

  const projectedCandles = buildProjectedCandles(candles, interval, normalizedDisplacement);
  const totalLength = projectedCandles.length;

  const tenkan: (number | undefined)[] = new Array(totalLength).fill(undefined);
  const kijun: (number | undefined)[] = new Array(totalLength).fill(undefined);
  const senkouA: (number | undefined)[] = new Array(totalLength).fill(undefined);
  const senkouB: (number | undefined)[] = new Array(totalLength).fill(undefined);
  const chikou: (number | undefined)[] = new Array(totalLength).fill(undefined);

  for (let index = 0; index < candles.length; index += 1) {
    const tenkanValue = midpointHighLow(candles, index, normalizedTenkanPeriod);
    if (tenkanValue !== undefined) {
      tenkan[index] = tenkanValue;
    }

    const kijunValue = midpointHighLow(candles, index, normalizedKijunPeriod);
    if (kijunValue !== undefined) {
      kijun[index] = kijunValue;
    }

    const senkouBBaseValue = midpointHighLow(candles, index, normalizedSenkouBPeriod);
    const senkouTargetIndex = index + normalizedDisplacement;
    if (senkouTargetIndex < totalLength) {
      if (tenkanValue !== undefined && kijunValue !== undefined) {
        senkouA[senkouTargetIndex] = (tenkanValue + kijunValue) / 2;
      }
      if (senkouBBaseValue !== undefined) {
        senkouB[senkouTargetIndex] = senkouBBaseValue;
      }
    }

    const chikouTargetIndex = index - normalizedDisplacement;
    if (chikouTargetIndex >= 0) {
      chikou[chikouTargetIndex] = candles[index].close;
    }
  }

  const bullishCloudUpper: (number | undefined)[] = new Array(totalLength).fill(undefined);
  const bullishCloudLower: (number | undefined)[] = new Array(totalLength).fill(undefined);
  const bearishCloudUpper: (number | undefined)[] = new Array(totalLength).fill(undefined);
  const bearishCloudLower: (number | undefined)[] = new Array(totalLength).fill(undefined);

  for (let index = 0; index < totalLength; index += 1) {
    const senkouAValue = senkouA[index];
    const senkouBValue = senkouB[index];
    if (
      senkouAValue === undefined ||
      senkouBValue === undefined ||
      !Number.isFinite(senkouAValue) ||
      !Number.isFinite(senkouBValue)
    ) {
      continue;
    }

    if (senkouAValue >= senkouBValue) {
      bullishCloudUpper[index] = senkouAValue;
      bullishCloudLower[index] = senkouBValue;
      continue;
    }

    bearishCloudUpper[index] = senkouBValue;
    bearishCloudLower[index] = senkouAValue;
  }

  return {
    projectedCandles,
    tenkan,
    kijun,
    senkouA,
    senkouB,
    chikou,
    bullishCloudUpper,
    bullishCloudLower,
    bearishCloudUpper,
    bearishCloudLower,
  };
}
